import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, User, UploadCloud, Save } from "lucide-react";
import { toast } from "sonner";
import { useRegistration } from "@/context/RegistrationContext";
import { reuploadReasonFor } from "./UploadCard";

const readAsDataURL = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

// Admin-requested correction card for candidate text fields (name, parent names)
// and the photograph — mirrors UploadCard's "reupload requested" visual state,
// but for fields/photo instead of document files.
export const CandidateFieldEditCard = ({ field }) => {
  const { state, updateCandidate, saveCandidateNow } = useRegistration();
  const c = state.candidate || {};
  const currentValue = c[field.id] || "";
  const [draft, setDraft] = useState(currentValue);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Only re-seed the draft when the server-side value itself changes (e.g. on
  // initial hydration) — never on the polling refresh, so an in-progress edit
  // is never clobbered mid-keystroke.
  useEffect(() => {
    setDraft(currentValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  const reason = reuploadReasonFor(state.history, field.id);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return toast.error("Please enter a value before saving");
    setSaving(true);
    const res = await saveCandidateNow({ [field.id]: trimmed });
    setSaving(false);
    if (res) toast.success(`${field.label} saved`);
    else toast.error("Failed to save — please try again");
  };

  const handlePhoto = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    const dataUrl = await readAsDataURL(file);
    updateCandidate({ photo: { name: file.name, size: file.size, type: file.type, dataUrl } });
  };

  return (
    <motion.div
      layout
      className="mu-card p-5 flex flex-col gap-4 border-orange-300 ring-2 ring-orange-100"
      data-testid={`field-edit-card-${field.id}`}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-[#111]">{field.label}</h3>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-800 bg-orange-100 ring-1 ring-inset ring-orange-300 px-2 py-1 rounded-full shrink-0" data-testid={`field-badge-reupload-${field.id}`}>
          <Clock className="h-3.5 w-3.5" /> Change requested
        </span>
      </div>

      <div className="rounded-xl bg-orange-50 border border-orange-200 p-3 text-xs text-orange-900" data-testid={`field-reupload-banner-${field.id}`}>
        <div className="font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> Admin requested a correction to {field.label}</div>
        {(reason || state.lastActionReason) && <div className="mt-1"><span className="font-semibold">Reason:</span> {reason || state.lastActionReason}</div>}
      </div>

      {field.type === "photo" ? (
        <div
          role="button"
          tabIndex={0}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handlePhoto(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          className={`grid grid-cols-1 sm:grid-cols-[auto_1fr] items-center gap-5 border-2 border-dashed rounded-2xl p-5 transition cursor-pointer ${
            dragOver ? "border-[#B60F2A] bg-[#FBE7EA]/60" : "border-[#E5E7EB] hover:border-[#B60F2A] hover:bg-[#FBE7EA]/30"
          }`}
          data-testid="field-photo-drop"
        >
          <div className="h-20 w-20 rounded-xl overflow-hidden bg-[#F7F7F7] border border-[#E5E7EB] flex items-center justify-center mx-auto sm:mx-0">
            {c.photo?.dataUrl || c.photo?.file_url ? (
              <img src={c.photo.dataUrl || c.photo.file_url} alt="Candidate" className="w-full h-full object-cover" />
            ) : (
              <User className="h-7 w-7 text-[#9CA3AF]" />
            )}
          </div>
          <div className="text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <UploadCloud className="h-4 w-4 text-[#B60F2A]" />
              <span className="text-sm font-medium text-[#111]">Drop new photo here or click to browse</span>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">Passport-size, JPG/PNG, up to 2 MB. Face clearly visible.</p>
            {c.photo?.uploading && <p className="text-xs text-[#B60F2A] mt-1">Uploading…</p>}
          </div>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e.target.files)} data-testid="field-photo-input" />
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="mu-input flex-1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            data-testid={`field-input-${field.id}`}
          />
          <button
            type="button"
            className="mu-btn-primary shrink-0"
            onClick={handleSave}
            disabled={saving || !draft.trim() || draft.trim() === currentValue}
            data-testid={`field-save-${field.id}`}
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </motion.div>
  );
};
